/**
 * Presence Context - Real-time Collaboration Indicators
 * 
 * Uses Supabase Realtime Presence to show:
 * - Who's viewing the same page
 * - Who's editing which row
 * - Active/idle status
 * 
 * Features:
 * - Automatic cleanup on disconnect
 * - Idle detection (2+ min inactive)
 * - Page-specific presence channels
 * - Row-level editing indicators
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { supabase, isSupabaseConfigured } from "../lib/supabase";
import { useAuth } from "./AuthContext";

// ============================================================
// CONSTANTS
// ============================================================
const IDLE_TIMEOUT = 2 * 60 * 1000; // 2 minutes
const HEARTBEAT_INTERVAL = 30 * 1000; // 30 seconds
const EDITING_TIMEOUT = 10 * 1000; // 10 seconds - clear editing state if no updates

// ============================================================
// CONTEXT
// ============================================================
const PresenceContext = createContext(null);

export const usePresence = () => {
  const context = useContext(PresenceContext);
  if (!context) {
    // Return safe defaults if not wrapped in provider
    return {
      users: [],
      editingRows: {},
      setCurrentPage: () => {},
      setEditingRow: () => {},
      clearEditingRow: () => {},
      isRowBeingEdited: () => null,
      isEnabled: false,
    };
  }
  return context;
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

// Generate a consistent color from email
function generateColorFromEmail(email) {
  if (!email) return '#6B7280';
  
  const colors = [
    '#EF4444', // red
    '#F97316', // orange
    '#F59E0B', // amber
    '#84CC16', // lime
    '#22C55E', // green
    '#14B8A6', // teal
    '#06B6D4', // cyan
    '#3B82F6', // blue
    '#6366F1', // indigo
    '#8B5CF6', // violet
    '#A855F7', // purple
    '#EC4899', // pink
  ];
  
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    hash = email.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
}

// Get initials from name
export function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// ============================================================
// PROVIDER
// ============================================================
export function PresenceProvider({ children }) {
  const { userName, userAvatar, userEmail, isAuthenticated, user } = useAuth();
  const [users, setUsers] = useState([]); // Users on current page
  const [editingRows, setEditingRows] = useState({}); // { rowId: { user, timestamp } }
  const [currentPage, setCurrentPageState] = useState(null);
  const [isIdle, setIsIdle] = useState(false);
  
  const channelRef = useRef(null);
  const idleTimerRef = useRef(null);
  const heartbeatRef = useRef(null);
  const userIdRef = useRef(null);
  const currentPageRef = useRef(null);
  const isIdleRef = useRef(false);
  
  // Store user data in refs to avoid dependency issues
  const userDataRef = useRef({
    name: 'Anonymous',
    email: null,
    avatar: null,
    color: '#6B7280',
    authUserId: null,
  });
  
  // Update refs when auth data changes
  useEffect(() => {
    if (!userIdRef.current && userEmail) {
      userIdRef.current = `${userEmail.replace(/[^a-zA-Z0-9]/g, '_')}-${Date.now().toString(36)}`;
    }
    userDataRef.current = {
      name: userName || userEmail?.split('@')[0] || 'Anonymous',
      email: userEmail,
      avatar: userAvatar,
      color: generateColorFromEmail(userEmail),
      authUserId: user?.id || null,
    };
    
    // Re-track presence when user data changes (especially when authUserId becomes available)
    if (channelRef.current && user?.id && currentPageRef.current) {
      console.log('[Presence] Re-tracking with authUserId:', user.id);
      channelRef.current.track({
        id: userIdRef.current,
        ...userDataRef.current,
        page: currentPageRef.current,
        status: isIdleRef.current ? 'idle' : 'active',
        lastActive: new Date().toISOString(),
        editingRow: null,
      }).catch(err => console.warn('Presence re-track error:', err));
    }
  }, [userName, userEmail, userAvatar, user]);
  
  // Keep refs in sync with state
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);
  
  useEffect(() => {
    isIdleRef.current = isIdle;
  }, [isIdle]);
  
  // Track presence using refs (no dependencies needed)
  const trackPresence = useCallback((editingRow = null) => {
    if (!channelRef.current || !userIdRef.current) return;
    
    channelRef.current.track({
      id: userIdRef.current,
      ...userDataRef.current,
      page: currentPageRef.current,
      status: isIdleRef.current ? 'idle' : 'active',
      lastActive: new Date().toISOString(),
      editingRow,
    }).catch(err => console.warn('Presence track error:', err));
  }, []);
  
  // Reset idle timer on activity
  const resetIdleTimer = useCallback(() => {
    if (isIdleRef.current) {
      setIsIdle(false);
    }
    
    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      setIsIdle(true);
    }, IDLE_TIMEOUT);
  }, []);
  
  // Set up activity listeners
  useEffect(() => {
    if (!isSupabaseConfigured() || !isAuthenticated) return;
    
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    const handleActivity = () => resetIdleTimer();
    
    events.forEach(event => window.addEventListener(event, handleActivity, { passive: true }));
    resetIdleTimer();
    
    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
      clearTimeout(idleTimerRef.current);
    };
  }, [resetIdleTimer, isAuthenticated]);
  
  // Subscribe to presence channel when page changes
  useEffect(() => {
    console.log('[Presence] Effect check - configured:', isSupabaseConfigured(), 'auth:', isAuthenticated, 'page:', currentPage, 'userId:', userIdRef.current);
    if (!isSupabaseConfigured() || !isAuthenticated || !currentPage || !userIdRef.current) {
      return;
    }
    
    // Cleanup previous channel
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    
    // Create channel for this page
    const channelName = `presence:${currentPage.replace(/[^a-zA-Z0-9-]/g, '_')}`;
    const channel = supabase.channel(channelName, {
      config: {
        presence: {
          key: userIdRef.current,
        },
      },
    });
    
    // Handle presence sync
    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        console.log('[Presence] Sync - raw state:', state);
        const presentUsers = [];
        const newEditingRows = {};
        
        Object.values(state).forEach((presences) => {
          presences.forEach((presence) => {
            // Don't include self in the list
            if (presence.id !== userIdRef.current) {
              const user = {
                id: presence.id,
                authUserId: presence.authUserId, // Supabase auth user ID for DMs
                name: presence.name,
                email: presence.email,
                avatar: presence.avatar,
                color: presence.color,
                status: presence.status || 'active',
                editingRow: presence.editingRow,
                lastActive: presence.lastActive,
              };
              presentUsers.push(user);
              
              // Track editing rows
              if (presence.editingRow) {
                newEditingRows[presence.editingRow] = {
                  user,
                  timestamp: presence.lastActive,
                };
              }
            }
          });
        });
        
        console.log('[Presence] Found other users:', presentUsers.length, presentUsers);
        setUsers(presentUsers);
        setEditingRows(newEditingRows);
      })
      .subscribe(async (status) => {
        console.log('[Presence] Channel status:', status, 'for page:', currentPage);
        if (status === 'SUBSCRIBED') {
          // Track our presence
          console.log('[Presence] Tracking user:', userIdRef.current, 'authUserId:', userDataRef.current.authUserId);
          await channel.track({
            id: userIdRef.current,
            ...userDataRef.current,
            page: currentPage,
            status: isIdleRef.current ? 'idle' : 'active',
            lastActive: new Date().toISOString(),
            editingRow: null,
          });
        }
      });
    
    channelRef.current = channel;
    
    // Heartbeat to keep presence alive
    heartbeatRef.current = setInterval(() => {
      trackPresence(null);
    }, HEARTBEAT_INTERVAL);
    
    return () => {
      clearInterval(heartbeatRef.current);
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [currentPage, isAuthenticated, trackPresence]);
  
  // Set current page
  const setCurrentPage = useCallback((page) => {
    console.log('[Presence] Setting current page:', page);
    setCurrentPageState(page);
  }, []);
  
  // Set editing row
  const setEditingRow = useCallback((rowId) => {
    trackPresence(rowId);
  }, [trackPresence]);
  
  // Clear editing row
  const clearEditingRow = useCallback(() => {
    trackPresence(null);
  }, [trackPresence]);
  
  // Check if a row is being edited by someone else
  const isRowBeingEdited = useCallback((rowId) => {
    const editing = editingRows[rowId];
    if (!editing) return null;
    
    // Check if the editing indicator is stale
    const elapsed = Date.now() - new Date(editing.timestamp).getTime();
    if (elapsed > EDITING_TIMEOUT) return null;
    
    return editing.user;
  }, [editingRows]);
  
  // Current user for display purposes
  const currentUser = {
    id: userIdRef.current,
    ...userDataRef.current,
  };
  
  return (
    <PresenceContext.Provider value={{
      users,
      editingRows,
      setCurrentPage,
      setEditingRow,
      clearEditingRow,
      isRowBeingEdited,
      currentUser,
      isEnabled: isSupabaseConfigured() && isAuthenticated,
    }}>
      {children}
    </PresenceContext.Provider>
  );
}

export default PresenceContext;
